import {
  CheckBadgeIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import { DocumentImage, DocumentImageOptimized } from "./utils";
import { useEffect, useState } from "react";
import axios from "../../../api/axios";
import { PMPagination } from "../../utils/PMPagination";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { DocumentUploadSectionV2 } from "../Media/DocumentUploadSectionV2";
import { EditDocumentDialog } from "../Media/editDocumentDialog";
import SimplePrompt from "../../utils/simplePrompt";
import { deleteDocument } from "../../../api/document";
import { dateToString } from "../../Utils";
import { TWclassNames } from "../../utils/Div";
import { DocumentUploadSectionV3 } from "../Media/DocumentUploadSectionV3";
import { DocumentUploadSectionV4 } from "../Media/DocumentUploadSectionV4";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";

export const DocumentCenter = ({ title = "Dokumenter", canUpload = true, ...props }) => {
  const { module, moduleParam, allowedFileTypes, subtitle } =
    props;

  const [page, setPage] = useState(1);
  const per_page = 20;

  const [state, setState] = useState({
    totalCount: 0,
    documents: [],
    document_pages: 0,
    isLoading: false,
  });

  const { documents, isLoading, totalCount } = state;

  const getDocuments = async () => {
    // setState({ ...state, isLoading: true });

    const params = {
      module: module,
      module_param: moduleParam,
      page: page,
      per_page: per_page,
    };
    const response = await axios.get(`/documents`, { params });
    setState({
      totalCount: response.data.totalCount,
      documents: response.data.documents,
      document_pages: response.data.pages - 1,
      isLoading: false,
    });
    setTimeout(() => {
      // setState({ ...state, isLoading: false });
    }, 50);
  };

  useEffect(() => {
    getDocuments();
  }, []);

  const handleAddDocuments = (data) => {
    setState({
      ...state,
      documents: [...documents, ...data],
    });
  };

  const handleDocumentChange = (document) => {
    // find existing document in list
    const index = documents.findIndex((x) => x.id === document.id);

    let prevItems = [...documents];
    prevItems[index] = document;
    setState({
      ...state,
      documents: prevItems,
    });
  };

  const [selectedEditDocument, setSelectedEditDocument] = useState(null);

  const [editDocumentDialogOpen, setEditDocumentDialogOpen] = useState(false);

  const [selectedRemoveDocument, setSelectedRemoveDocument] = useState(null);

  const [submitRemoveDocumentPromptOpen, setSubmitRemoveDocumentOpen] =
    useState(false);

  const submitRemoveDocument = async (document) => {
    await deleteDocument(document?.id).then((res) => {
      // check if any of these statuses 200, 204 etc.
      if (res.status === 204 || res.status === 200) {
        const newDocuments = documents.filter(
          (item) => item.id !== document.id
        );
        setState({
          ...state,
          documents: newDocuments,
        });
      }
    });
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center">
        <div className="flex flex-col">
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-sm text-gray-500"> {subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {canUpload && (
            <DocumentUploadSectionV4
              allowedFileTypes={allowedFileTypes}
              module={module}
              moduleParam={moduleParam}
              parentCallback={(data) =>
                // add to state >> documents
                {
                  handleAddDocuments(data);
                }
              }
            />
          )}
        </div>
      </div>

      {/* Documents Stacklist */}
      {documents?.length > 0 ? (
        <>
          <div className="flex flex-col gap-2 py-5">
            {documents.map((document) => (
              <div
                key={document.id}
                className="flex items-center justify-between w-full p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex">
                  <div className="h-32 w-32 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                    <DocumentImageOptimized
                      document={document}
                      fallback={
                        document?.file?.type === "application/pdf"
                          ? PdfPlaceholder
                          : null
                      }
                    />
                  </div>
                  <div className="flex left flex-col gap-2 px-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {document?.title || "Missing title"}
                    </h2>

                    <p className="text-sm text-gray-600 font-medium">{document?.file?.filename}</p>

                    <p className="text-sm text-gray-500">
                      Created at: {dateToString(document?.created_at)}
                    </p>

                    <div className="flex items-center">
                      {document?.public ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckBadgeIcon className="h-4 w-4 mr-1.5" /> Public
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <ExclamationCircleIcon className="h-4 w-4 mr-1.5" /> Private
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <PMButton
                    styleType="minimal"
                    className="border rounded-md border-gray-200 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors duration-200"
                    onClick={() => {
                      setSelectedEditDocument(document);
                      setEditDocumentDialogOpen(true);
                    }}
                  >
                    <PencilIcon className="h-4 w-4 mr-1.5" />
                    <span>Rediger</span>
                  </PMButton>
                  <PMButton
                    styleType="minimal"
                    className="border rounded-md border-gray-200 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors duration-200"
                    onClick={() => {
                      setSelectedRemoveDocument(document);
                      setSubmitRemoveDocumentOpen(true);
                    }}
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    <span>Slett</span>
                  </PMButton>
                </div>
              </div>
            ))}
          </div>

          <div>
            <PMPagination
              perPage={per_page}
              onPageChange={(page) => setPage(page)}
              totalCount={totalCount}
              currentCount={documents.length}
            />
          </div>

          <SimplePrompt
            title="Sikker på at du vill slette dette dokumentet?"
            description="Dokumentet slettes permanent og kan ikke gjenopprettes."
            onAccept={() => submitRemoveDocument(selectedRemoveDocument)}
            open={submitRemoveDocumentPromptOpen}
            onClose={() => setSubmitRemoveDocumentOpen(false)}
          />

          <EditDocumentDialog
            document={selectedEditDocument}
            open={editDocumentDialogOpen}
            onClose={() => setEditDocumentDialogOpen(false)}
            onRefresh={(data) => {
              handleDocumentChange(data);
            }}
            editPublic={true}
          />
        </>
      ) : (
        <ResourceEmpty text="Ingen filer tilgjengelig" />
      )}
    </div>
  );
};
